import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Pill } from "@/components/perf";
import ProductMediaEditor, { readResized } from "@/components/ProductMediaEditor";
import ProductPricingEditor from "@/components/ProductPricingEditor";
import { Package, Plus, Trash2, Save, Upload, Link2, ImageIcon, ChevronDown, Building2, Truck, Images } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Paddles", "Balls", "Grips", "Training Aids", "Nets", "Apparel", "Bags", "Recovery"];
const LEVELS = ["All", "Low Intermediate", "Intermediate", "Advanced"];
const EMPTY = { name: "", company: "", category: "Paddles", price: "", price_coins: "", level: "All", style: "all", image: "", images: [], sizes: [], colors: [], color_photos: {}, options: [], tax_rules: [], desc: "", stock_quantity: "", ambassador_link: "", requires_shipping: true, shipping_price: "" };
const NO_COMPANY = "Other / Unbranded";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function InventoryManager() {
  const [products, setProducts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [openCompanies, setOpenCompanies] = useState({});
  const [openCats, setOpenCats] = useState({});
  const [mediaRow, setMediaRow] = useState(null);
  const [mediaDraft, setMediaDraft] = useState(null);

  const openMedia = (p) => {
    setMediaRow(p.id);
    setMediaDraft({ images: p.images?.length ? p.images : [p.image].filter(Boolean), image: p.image, sizes: p.sizes || [], colors: p.colors || [], color_photos: p.color_photos || {}, options: p.options || [], tax_rules: p.tax_rules || [], desc: p.desc || "" });
  };
  const saveMedia = async (p) => {
    setBusyId(p.id);
    try {
      const body = { ...p, ...mediaDraft, price: Number(drafts[p.id]?.price ?? p.price),
        price_coins: Number(drafts[p.id]?.price_coins ?? p.price_coins) || null,
        stock_quantity: Number(drafts[p.id]?.stock_quantity ?? p.stock_quantity),
        shipping_price: Number(drafts[p.id]?.shipping_price ?? p.shipping_price) || null,
        ambassador_link: (drafts[p.id]?.ambassador_link ?? p.ambassador_link) || "",
        company: (drafts[p.id]?.company ?? p.company) || "" };
      delete body.id; delete body.sold_out; delete body.low; delete body.linked_orders;
      await api.put(`/admin/products/${p.id}`, body);
      toast.success(`${p.name}: photos & options saved`);
      setMediaRow(null);
      await load();
    } catch { toast.error("Update failed"); }
    finally { setBusyId(null); }
  };

  const load = async () => {
    const { data } = await api.get("/admin/products");
    setProducts(data);
    const d = {};
    data.forEach((p) => { d[p.id] = { price: p.price, price_coins: p.price_coins ?? "", stock_quantity: p.stock_quantity, ambassador_link: p.ambassador_link || "", company: p.company || "", shipping_price: p.shipping_price ?? "" }; });
    setDrafts(d);
  };
  useEffect(() => { load(); }, []);

  const setDraft = (id, key, val) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: val } }));

  const saveRow = async (p) => {
    setBusyId(p.id);
    try {
      const body = { ...p, price: Number(drafts[p.id].price), stock_quantity: Number(drafts[p.id].stock_quantity),
        price_coins: Number(drafts[p.id].price_coins) || null,
        shipping_price: Number(drafts[p.id].shipping_price) || null,
        ambassador_link: (drafts[p.id].ambassador_link || "").trim(),
        company: (drafts[p.id].company || "").trim() };
      delete body.id; delete body.sold_out; delete body.low;
      await api.put(`/admin/products/${p.id}`, body);
      toast.success(`${p.name} updated`);
      await load();
    } catch { toast.error("Update failed"); }
    finally { setBusyId(null); }
  };

  const toggleShipping = async (p) => {
    setBusyId(p.id);
    try {
      const body = { ...p, requires_shipping: p.requires_shipping === false };
      delete body.id; delete body.sold_out; delete body.low;
      await api.put(`/admin/products/${p.id}`, body);
      toast.success(`${p.name}: shipping ${p.requires_shipping === false ? "will now apply" : "removed — no shipping charged"}`);
      await load();
    } catch { toast.error("Update failed"); }
    finally { setBusyId(null); }
  };

  const changeRowImage = async (p, file) => {
    if (!file) return;
    setBusyId(p.id);
    try {
      const image = await readResized(file);
      const rest = (p.images?.length ? p.images : [p.image].filter(Boolean)).slice(1);
      const body = { ...p, image, images: [image, ...rest], price: Number(drafts[p.id]?.price ?? p.price),
        price_coins: Number(drafts[p.id]?.price_coins ?? p.price_coins) || null,
        stock_quantity: Number(drafts[p.id]?.stock_quantity ?? p.stock_quantity),
        ambassador_link: (drafts[p.id]?.ambassador_link ?? p.ambassador_link) || "",
        company: (drafts[p.id]?.company ?? p.company) || "" };
      delete body.id; delete body.sold_out; delete body.low;
      await api.put(`/admin/products/${p.id}`, body);
      toast.success(`${p.name} image updated`);
      await load();
    } catch { toast.error("Image update failed"); }
    finally { setBusyId(null); }
  };

  const removeRow = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This removes it from the store.`)) return;
    setBusyId(p.id);
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success(`${p.name} deleted`);
      await load();
    } catch { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  const create = async () => {
    if (!form.name || !form.category || form.price === "" || form.stock_quantity === "")
      return toast.error("Fill name, category, price and stock");
    setSaving(true);
    try {
      await api.post("/admin/products", {
        ...form, price: Number(form.price), stock_quantity: Number(form.stock_quantity),
        price_coins: Number(form.price_coins) || null,
        shipping_price: form.requires_shipping ? Number(form.shipping_price) || null : null,
        ambassador_link: form.ambassador_link.trim(), company: form.company.trim(),
      });
      toast.success("Product added");
      setForm(EMPTY);
      await load();
    } catch { toast.error("Could not add product"); }
    finally { setSaving(false); }
  };

  const companies = [...new Set(products.map((p) => p.company || NO_COMPANY))]
    .sort((a, b) => (a === NO_COMPANY ? 1 : b === NO_COMPANY ? -1 : a.localeCompare(b)));
  const companyNames = [...new Set(products.map((p) => p.company).filter(Boolean))];

  const renderRow = (p) => (
    <div key={p.id} data-testid={`admin-product-${p.id}`} className="px-5 py-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-3 min-w-0">
          <RowImage p={p} busy={busyId === p.id} onFile={(f) => changeRowImage(p, f)} />
          <div className="min-w-0">
            <div className="font-semibold truncate">{p.name}</div>
            <div className="text-xs text-zinc-500">{p.category} · {p.linked_orders ?? 0} ordered</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid={`row-media-btn-${p.id}`} onClick={() => (mediaRow === p.id ? setMediaRow(null) : openMedia(p))}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-widest border transition-colors ${mediaRow === p.id ? "bg-pb-red text-black border-pb-red" : "border-pb-border text-zinc-400 hover:text-white hover:border-pb-red"}`}>
            <Images className="w-3.5 h-3.5" /> {(p.images?.length || (p.image ? 1 : 0))} photo{(p.images?.length || (p.image ? 1 : 0)) === 1 ? "" : "s"}{(p.sizes?.length || p.colors?.length) ? ` · ${(p.sizes || []).length}S/${(p.colors || []).length}C` : ""}{p.options?.length ? ` · ${p.options.length} prices` : ""}{p.tax_rules?.length ? ` · tax ${p.tax_rules.map((r) => r.region).join("/")}` : ""}
          </button>
          {p.ambassador_link && <Pill color="#4ADE80">Ambassador</Pill>}
          {p.sold_out ? <Pill color="#EF4444">Sold Out</Pill> : p.low ? <Pill color="#F59E0B">Low</Pill> : <Pill color="#4ADE80">OK</Pill>}
        </div>
      </div>
      {mediaRow === p.id && mediaDraft && (
        <div className="mb-3 border border-pb-red/40 bg-black/40 p-4 space-y-3" data-testid={`row-media-panel-${p.id}`}>
          <ProductMediaEditor value={mediaDraft} onChange={setMediaDraft} testid={`row-media-${p.id}`} />
          <ProductPricingEditor value={mediaDraft} onChange={setMediaDraft} testid={`row-pricing-${p.id}`} />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Full Description</label>
            <textarea data-testid={`row-desc-${p.id}`} value={mediaDraft.desc} rows={3} onChange={(e) => setMediaDraft({ ...mediaDraft, desc: e.target.value })}
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white text-sm focus:outline-none focus:border-pb-red resize-y" />
          </div>
          <div className="flex gap-2">
            <button data-testid={`row-media-save-${p.id}`} onClick={() => saveMedia(p)} disabled={busyId === p.id}
              className="flex items-center gap-1.5 bg-pb-red text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#BEF264] disabled:opacity-50"><Save className="w-3.5 h-3.5" /> Save Photos & Options</button>
            <button data-testid={`row-media-cancel-${p.id}`} onClick={() => setMediaRow(null)} className="border border-pb-border px-4 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white">Cancel</button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[10px] uppercase tracking-widest text-zinc-500">$</label>
        <input data-testid={`edit-price-${p.id}`} type="number" value={drafts[p.id]?.price ?? ""} onChange={(e) => setDraft(p.id, "price", e.target.value)}
          className="w-24 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-sm focus:outline-none focus:border-pb-red" />
        <label className="text-[10px] uppercase tracking-widest text-purple-300 ml-2">🪙</label>
        <input data-testid={`edit-coins-${p.id}`} type="number" placeholder="—" value={drafts[p.id]?.price_coins ?? ""} onChange={(e) => setDraft(p.id, "price_coins", e.target.value)}
          className="w-20 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-purple-400" />
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">Stock</label>
        <input data-testid={`edit-stock-${p.id}`} type="number" value={drafts[p.id]?.stock_quantity ?? ""} onChange={(e) => setDraft(p.id, "stock_quantity", e.target.value)}
          className="w-20 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-sm focus:outline-none focus:border-pb-red" />
        <div className="flex items-center gap-1.5 min-w-[140px]">
          <Building2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
          <input data-testid={`edit-company-${p.id}`} list="company-options" value={drafts[p.id]?.company ?? ""} placeholder="Company"
            onChange={(e) => setDraft(p.id, "company", e.target.value)}
            className="w-full bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-xs placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
        </div>
        <div className="flex items-center gap-1.5 flex-1 min-w-[160px]">
          <Link2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
          <input data-testid={`edit-ambassador-${p.id}`} value={drafts[p.id]?.ambassador_link ?? ""} placeholder="Ambassador link (optional)"
            onChange={(e) => setDraft(p.id, "ambassador_link", e.target.value)}
            className="w-full bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-xs placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
        </div>
        <button data-testid={`toggle-shipping-${p.id}`} onClick={() => toggleShipping(p)} disabled={busyId === p.id}
          title={p.requires_shipping !== false ? "Shipping applies — click to make this a no-shipping item" : "No shipping charged — click to apply shipping"}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-widest border transition-colors disabled:opacity-50 ${
            p.requires_shipping !== false ? "border-pb-cyan/60 text-pb-cyan" : "border-zinc-700 text-zinc-500"}`}>
          <Truck className="w-3.5 h-3.5" /> {p.requires_shipping !== false ? "Ships" : "No Ship"}
        </button>
        {p.requires_shipping !== false && (
          <input data-testid={`edit-shipprice-${p.id}`} type="number" placeholder="def" title="Shipping price ($ — blank = site default)"
            value={drafts[p.id]?.shipping_price ?? ""} onChange={(e) => setDraft(p.id, "shipping_price", e.target.value)}
            className="w-16 bg-pb-elevated border border-pb-cyan/40 px-2 py-1.5 text-pb-cyan text-xs placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
        )}
        <button data-testid={`save-product-${p.id}`} onClick={() => saveRow(p)} disabled={busyId === p.id}
          className="ml-auto flex items-center gap-1.5 bg-pb-elevated border border-pb-border hover:border-pb-red px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        <button data-testid={`delete-product-${p.id}`} onClick={() => removeRow(p)} disabled={busyId === p.id}
          className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-8" data-testid="inventory-manager">
      <h3 className="font-display font-bold text-2xl uppercase mb-1 flex items-center gap-2">
        <Package className="w-6 h-6 text-pb-red" /> Inventory &amp; Catalog
      </h3>
      <p className="text-sm text-zinc-500 mb-5">Products are grouped by company, then by category — click a company to open its categories, and a category to see its products. Stock auto-reduces on each sale; 0 stock shows as “Sold Out” in the store.</p>

      <datalist id="company-options">
        {companyNames.map((c) => <option key={c} value={c} />)}
      </datalist>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Add product */}
        <div className="bg-pb-surface border border-pb-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <Plus className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">New Product</span>
          </div>
          <div className="space-y-3">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Bullpen Carbon Pro" testid="prod-name" />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Company / Brand</label>
              <input data-testid="prod-company" list="company-options" value={form.company} placeholder="Selkirk, JOOLA, Bullpen…"
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
            </div>
            <CategoryPicker value={form.category} products={products} onChange={(v) => setForm({ ...form, category: v })} testid="prod-category" />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Price ($)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} placeholder="179.99" testid="prod-price" />
              <Field label="Coin Price (BLC)" type="number" value={form.price_coins} onChange={(v) => setForm({ ...form, price_coins: v })} placeholder="optional" testid="prod-price-coins" />
              <Field label="Stock" type="number" value={form.stock_quantity} onChange={(v) => setForm({ ...form, stock_quantity: v })} placeholder="25" testid="prod-stock" />
            </div>
            <p className="text-[10px] text-zinc-600 -mt-1">Set a BLC coin price to let members pay with BULLION (or split coin + card). Leave blank for card-only.</p>
            <Select label="Skill Level" value={form.level} options={LEVELS} onChange={(v) => setForm({ ...form, level: v })} testid="prod-level" />

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Shipping</label>
              <div className="flex">
                {[[true, "Charge Shipping", "physical item — ships to the member"], [false, "No Shipping", "digital / pickup / service — never adds shipping at checkout"]].map(([val, label]) => (
                  <button key={String(val)} type="button" data-testid={`prod-shipping-${val ? "on" : "off"}`}
                    onClick={() => setForm({ ...form, requires_shipping: val })}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-widest border transition-colors ${
                      form.requires_shipping === val ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                    <Truck className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
              {form.requires_shipping && (
                <div className="mt-2">
                  <Field label="Shipping Price ($ — blank = site default)" type="number" value={form.shipping_price}
                    onChange={(v) => setForm({ ...form, shipping_price: v })} placeholder="8.99" testid="prod-shipping-price" />
                </div>
              )}
              <p className="text-[10px] text-zinc-600 mt-1.5">{form.requires_shipping ? "Shipping is charged at checkout unless the cart's shippable gear clears the free-shipping threshold. If the cart holds multiple shippable items, the highest shipping rate applies once." : "This product will never add shipping cost at checkout."}</p>
            </div>

            <ProductMediaEditor value={form} onChange={setForm} testid="prod-media" />
            <ProductPricingEditor value={form} onChange={setForm} testid="prod-pricing" />

            <div>
              <Field label="Ambassador Link (optional)" value={form.ambassador_link} onChange={(v) => setForm({ ...form, ambassador_link: v })} placeholder="https://ambassador-site.com/product" testid="prod-ambassador-link" />
              <p className="text-[10px] text-zinc-600 mt-1">If set, the store shows a “Buy Now” button that opens this link in a new tab (no cart checkout).</p>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Full Description</label>
              <textarea data-testid="prod-desc" value={form.desc} rows={4} placeholder="Tell players everything — materials, weight, fit, what it's best for. Shoppers see the full text when they enlarge a photo."
                onChange={(e) => setForm({ ...form, desc: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-pb-red resize-y" />
            </div>
            <button data-testid="create-product-btn" onClick={create} disabled={saving}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              {saving ? "Adding…" : "Add Product"}
            </button>
          </div>
        </div>

        {/* Catalog grouped by company -> category */}
        <div className="lg:col-span-2 bg-pb-surface border border-pb-border">
          <div className="flex items-center justify-between p-5 border-b border-pb-border">
            <span className="font-display font-bold text-lg uppercase">Catalog ({products.length})</span>
            <span className="text-xs text-zinc-500 font-mono">company → category → products</span>
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {products.length === 0 && <div className="p-5 text-sm text-zinc-600">No products yet.</div>}
            {companies.map((co) => {
              const coSlug = slugify(co);
              const coProducts = products.filter((p) => (p.company || NO_COMPANY) === co);
              const cats = [...new Set(coProducts.map((p) => p.category))].sort();
              const coOpen = !!openCompanies[co];
              return (
                <div key={co} className="border-b border-pb-border/50 last:border-b-0">
                  <button data-testid={`company-toggle-${coSlug}`} onClick={() => setOpenCompanies((o) => ({ ...o, [co]: !o[co] }))}
                    className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-pb-elevated/40 transition-colors">
                    <Building2 className="w-4 h-4 text-pb-red shrink-0" />
                    <span className="font-display font-bold uppercase text-base flex-1 min-w-0 truncate">{co}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-600 shrink-0">{coProducts.length} product{coProducts.length === 1 ? "" : "s"} · {cats.length} categor{cats.length === 1 ? "y" : "ies"}</span>
                    <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform duration-200 ${coOpen ? "rotate-180" : ""}`} />
                  </button>
                  {coOpen && cats.map((cat) => {
                    const key = `${co}|${cat}`;
                    const catOpen = !!openCats[key];
                    const catProducts = coProducts.filter((p) => p.category === cat);
                    return (
                      <div key={cat} className="border-t border-pb-border/40">
                        <button data-testid={`category-toggle-${coSlug}-${slugify(cat)}`} onClick={() => setOpenCats((o) => ({ ...o, [key]: !o[key] }))}
                          className="w-full flex items-center gap-2 pl-12 pr-5 py-2.5 text-left hover:bg-pb-elevated/30 transition-colors">
                          <span className="text-xs font-bold uppercase tracking-widest text-pb-cyan flex-1">{cat}</span>
                          <span className="text-[10px] text-zinc-600 shrink-0">{catProducts.length}</span>
                          <ChevronDown className={`w-3.5 h-3.5 text-zinc-600 shrink-0 transition-transform duration-200 ${catOpen ? "rotate-180" : ""}`} />
                        </button>
                        {catOpen && <div className="divide-y divide-pb-border/40 bg-pb-elevated/20">{catProducts.map(renderRow)}</div>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function RowImage({ p, busy, onFile }) {
  const ref = useRef(null);
  return (
    <div className="relative shrink-0 group/img">
      {p.image ? (
        <img src={p.image} alt={p.name} className="w-12 h-12 object-cover border border-pb-border" />
      ) : (
        <div className="w-12 h-12 bg-pb-elevated border border-pb-border flex items-center justify-center"><ImageIcon className="w-5 h-5 text-zinc-600" /></div>
      )}
      <button data-testid={`row-image-btn-${p.id}`} onClick={() => ref.current?.click()} disabled={busy} title="Change image"
        className="absolute inset-0 bg-black/60 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity">
        <Upload className="w-4 h-4 text-pb-red" />
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`row-image-input-${p.id}`}
        onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
    </div>
  );
}

function CategoryPicker({ value, products, onChange, testid }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const cats = [...new Set([...CATEGORIES, ...products.map((p) => p.category).filter(Boolean), value].filter(Boolean))];
  const commit = () => {
    const c = draft.trim().replace(/\s+/g, " ").slice(0, 40);
    if (!c) return;
    onChange(c); setDraft(""); setAdding(false);
    toast.success(`Category "${c}" ready — it appears in the shop once a product is saved with it`);
  };
  return (
    <div>
      <label className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
        <span>Category</span>
        <button type="button" data-testid={`${testid}-new`} onClick={() => setAdding(!adding)} className="text-pb-cyan hover:text-white normal-case tracking-normal font-semibold">{adding ? "Cancel" : "+ New category"}</button>
      </label>
      {adding ? (
        <div className="flex gap-1.5">
          <input autoFocus data-testid={`${testid}-input`} value={draft} placeholder="e.g. Eyewear, Shoes, Court Equipment" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") setAdding(false); }}
            className="flex-1 min-w-0 bg-pb-elevated border border-pb-cyan px-3 py-2 text-white placeholder-zinc-600 text-sm focus:outline-none" />
          <button type="button" data-testid={`${testid}-add`} onClick={commit} className="bg-pb-cyan text-black px-3 text-xs font-black uppercase tracking-widest">Add</button>
        </div>
      ) : (
        <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
          {cats.map((o) => <option key={o} value={o}>{o}{!CATEGORIES.includes(o) ? " (custom)" : ""}</option>)}
        </select>
      )}
    </div>
  );
}

function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
